'use client';

import type { FC } from 'react';

import { LayoutContainer, Section, TextPassage } from '@petsmart-ui/sparky';

import { trackTile6UpClick, trackTilesClicks } from '@libs/analytics';
import { usePathname } from 'next/navigation';
import { clsx, usePageLayout } from '@libs/common-next';
import type { Tile6UpVariant } from '../../types';
import type { Tile6UpProps } from './types';

import Tile6UpMultirow from './Tile6UpMultirow';
import Tile6UpPromo from './Tile6UpPromo';
import Tile6UpShoppable from './Tile6UpShoppable';
import Tile6UpTips from './Tile6UpTips';
import Tile6UpVisualNav from './Tile6UpVisualNav';
import { ColoredTextLink } from '../ColoredTextLink';

import './Tile6Up.scss';

const ComponentTile6Up: Record<Tile6UpVariant, FC<Tile6UpProps>> = {
    Multirow: Tile6UpMultirow,
    Promo: Tile6UpPromo,
    Shoppable: Tile6UpShoppable,
    VisualNav: Tile6UpVisualNav,
    Tips: Tile6UpTips,
};

export default function Tile6Up({
    variant,
    header,
    linkAction,
    linkActionColor,
    tiles,
    description,
    alignText,
}: Tile6UpProps) {
    const Component = ComponentTile6Up[variant];
    const { flags } = usePageLayout();
    const { redesignedComponentsEnabled } = flags;
    const pathname = usePathname();

    // To avoid rendering the component if there are no tiles to show
    if (tiles?.length === 0) {
        return null;
    }

    const isCentered = alignText !== 'left' && Boolean(redesignedComponentsEnabled);
    const { link, actionText } = linkAction || {};

    const restyledVariants = ['Multirow', 'Promo', 'Shoppable', 'Tips'];
    const isRestyled = restyledVariants.includes(variant) && Boolean(redesignedComponentsEnabled);

    const componentClassName = clsx(
        `tile6up variant-${variant.toLowerCase()}`,
        !header && 'no-tile-header',
        isCentered && 'title-center',
        isRestyled && `tile6up--restyled`
    );

    return (
        <LayoutContainer className="tile6up-container">
            <Section
                data-testid="tile6up-test-id"
                title={header}
                description={!isCentered ? description : ''}
                {...(isRestyled && {
                    align: isCentered ? 'center' : undefined,
                    titleAfterLocation: 'below-description',
                })}
                titleAfter={
                    <>
                        {isCentered && description && <TextPassage>{description}</TextPassage>}
                        {link && actionText && (
                            <ColoredTextLink
                                data-testid="tile6up-link-action"
                                href={link?.webUrl}
                                onClick={() => {
                                    trackTile6UpClick({
                                        variant,
                                        name: actionText,
                                        position: null,
                                        navigationClick: null,
                                    });

                                    if (variant === 'Multirow') {
                                        trackTilesClicks({
                                            variant,
                                            header: header ?? '',
                                            title: actionText,
                                            position: -1,
                                            row: -1,
                                        });
                                    }
                                }}
                                color={linkActionColor}
                            >
                                {actionText}
                            </ColoredTextLink>
                        )}
                    </>
                }
                className={componentClassName}
            >
                <Component
                    variant={variant}
                    header={header}
                    linkAction={linkAction}
                    tiles={tiles}
                    pathname={pathname || ''}
                />
            </Section>
        </LayoutContainer>
    );
}
